import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';
import { Issue } from '../../../setup/types/core';
import { AfterDoneResult } from '../../issue/wizard/afterDoneResult';
import { useProjectId } from 'store/project';
import { CreateIssueDialog } from '../../issue/wizard/createIssueDialog';
import { useAppNavigation } from 'contexts/navigation';

const CreateIssueServiceContext = createContext<() => void>(() => {});

export const useCreateIssue = (): (() => void) =>
  useContext(CreateIssueServiceContext);

export function useRedirectToIssue(): (
  issueId: string,
  // ready to take issue from state (local changes) instead of url
  issue?: any
) => void {
  const { goToIssueDetails } = useAppNavigation();
  const projectId = useProjectId();

  return useCallback(
    (issueId, issue) => {
      goToIssueDetails(projectId, issueId, {
        state: { issue },
      });
    },
    [goToIssueDetails, projectId]
  );
}

export const CreateIssueContext = ({
  children,
}: PropsWithChildren<{}>): React.ReactElement => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const redirectToIssue = useRedirectToIssue();

  const openDialog = useCallback(() => {
    setDialogOpen(true);
  }, []);
  const closeDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleAfterDone = useCallback(
    (issue: Issue | undefined, result: AfterDoneResult) => {
      if (result === AfterDoneResult.saved && issue) {
        redirectToIssue(issue._id, issue);
      } else {
        closeDialog();
      }
    },
    [redirectToIssue, closeDialog]
  );

  return (
    <CreateIssueServiceContext.Provider value={openDialog}>
      {children}
      <CreateIssueDialog afterDone={handleAfterDone} open={dialogOpen} />
    </CreateIssueServiceContext.Provider>
  );
};
